/**
 * Unilar Carousel Banner
 * Initializes FancyApps Carousel on <card-slider> elements,
 * with optional self-sustaining autoplay (data-autoplay="true").
 * Slides containing <banner-video> play only while current; with autoplay,
 * the carousel waits for the video to end instead of using the fixed delay.
 */

if (!customElements.get('card-slider')) {
  /** Time to wait for a video slide to start before falling back to the fixed delay. */
  const VIDEO_START_TIMEOUT_MS = 8000;

  class CardSlider extends HTMLElement {
    /** @type {import('fancyapps').Carousel | null} */
    #carousel = null;

    /** @type {ReturnType<typeof setTimeout> | null} */
    #timer = null;

    /** @type {number} */
    #delayMs = 5000;

    /** @type {number} */
    #remaining = 5000;

    /** @type {number} */
    #timerStart = 0;

    /** @type {boolean} */
    #isPaused = false;

    /** @type {boolean} */
    #ignoreHoverPause = false;

    /** @type {boolean} */
    #autoplayEnabled = false;

    /** @type {(() => void) | null} */
    #onVisibilityChange = null;

    /** @type {IntersectionObserver | null} */
    #intersectionObserver = null;

    /** @type {boolean} */
    #hasActivated = false;

    /** @type {number | null} */
    #rafId = null;

    /** @type {number | null} */
    #progressRafId = null;

    /** @type {Set<HTMLElement>} */
    #managedSlides = new Set();

    /** @type {number | null} */
    #slideAccessibilityRafId = null;

    /** @type {boolean} */
    #videoMode = false;

    /** @type {ReturnType<typeof setTimeout> | null} */
    #videoStartTimer = null;

    /** @type {(() => void) | null} */
    #videoStartOnLoad = null;

    #getCurrentSlideEl() {
      const carousel = this.#carousel;
      if (!carousel) return null;
      return carousel.pages?.[carousel.page]?.slides?.[0]?.el ?? this.querySelector('.f-carousel__slide');
    }

    /** @returns {(HTMLElement & { failed?: boolean }) | null} */
    #getCurrentVideo() {
      return this.#getCurrentSlideEl()?.querySelector('banner-video') ?? null;
    }

    #syncVideos = () => {
      const currentSlide = this.#getCurrentSlideEl();

      this.querySelectorAll('banner-video').forEach((video) => {
        if (typeof video.setActive === 'function') {
          video.setActive(Boolean(currentSlide && currentSlide.contains(video)));
        }
      });
    };

    /** @param {Event} event */
    #isFromCurrentVideo(event) {
      const current = this.#getCurrentVideo();
      return Boolean(current && event.target === current);
    }

    #onVideoPlaying = (event) => {
      if (!this.#autoplayEnabled || !this.#isFromCurrentVideo(event)) return;
      this.#enterVideoMode();
    };

    #onVideoProgress = (event) => {
      if (!this.#videoMode || !this.#isFromCurrentVideo(event)) return;
      this.style.setProperty('--banner-video-progress', String(event.detail?.progress ?? 0));
    };

    #onVideoEnded = (event) => {
      if (!this.#autoplayEnabled || this.#isPaused || !this.#isFromCurrentVideo(event)) return;
      event.preventDefault();
      this.#advance();
    };

    #onVideoError = (event) => {
      if (!this.#autoplayEnabled || !this.#isFromCurrentVideo(event)) return;
      this.#beginCycle();
    };

    #enterVideoMode() {
      this.#clearVideoStartTimer();
      this.#stopTimer();
      this.#cancelProgressRestart();
      this.#videoMode = true;
      this.classList.remove('is-autoplay-running');
      this.classList.add('is-video-slide');
    }

    #exitVideoMode() {
      this.#clearVideoStartTimer();
      this.#videoMode = false;
      this.classList.remove('is-video-slide');
      this.style.removeProperty('--banner-video-progress');
    }

    #clearVideoStartTimer() {
      if (this.#videoStartOnLoad) {
        window.removeEventListener('load', this.#videoStartOnLoad);
        this.#videoStartOnLoad = null;
      }

      if (this.#videoStartTimer === null) return;
      clearTimeout(this.#videoStartTimer);
      this.#videoStartTimer = null;
    }

    #isWaitingForVideo() {
      return this.#videoStartTimer !== null || this.#videoStartOnLoad !== null;
    }

    /** Videos only start loading after window load, so the start timeout counts from there. */
    #scheduleVideoStartTimeout() {
      const startTimeout = () => {
        this.#videoStartOnLoad = null;
        this.#videoStartTimer = setTimeout(() => {
          this.#videoStartTimer = null;
          this.#beginTimedCycle();
        }, VIDEO_START_TIMEOUT_MS);
      };

      if (document.readyState === 'complete') {
        startTimeout();
        return;
      }

      this.#videoStartOnLoad = startTimeout;
      window.addEventListener('load', startTimeout, { once: true });
    }

    #syncSlideInert = () => {
      if (!this.#carousel || typeof this.#carousel.getVisibleSlides !== 'function') {
        return;
      }

      const visibleSlides = this.#carousel.getVisibleSlides(0);

      this.#carousel.slides.forEach((slide) => {
        const element = slide.el;
        if (!(element instanceof HTMLElement)) return;

        this.#managedSlides.add(element);

        if (visibleSlides.has(slide)) {
          element.removeAttribute('inert');
          element.setAttribute('aria-hidden', 'false');
        } else {
          element.setAttribute('inert', '');
          element.setAttribute('aria-hidden', 'true');
        }
      });
    };

    #scheduleSlideAccessibility = () => {
      this.#cancelSlideAccessibility();

      this.#slideAccessibilityRafId = requestAnimationFrame(() => {
        this.#slideAccessibilityRafId = null;
        this.#syncSlideInert();
      });
    };

    #cancelSlideAccessibility() {
      if (this.#slideAccessibilityRafId === null) return;

      cancelAnimationFrame(this.#slideAccessibilityRafId);
      this.#slideAccessibilityRafId = null;
    }

    connectedCallback() {
      this.addEventListener('banner-video:connected', this.#syncVideos);
      this.addEventListener('banner-video:playing', this.#onVideoPlaying);
      this.addEventListener('banner-video:progress', this.#onVideoProgress);
      this.addEventListener('banner-video:ended', this.#onVideoEnded);
      this.addEventListener('banner-video:error', this.#onVideoError);

      if (
        this.dataset.lazyInit !== 'true' ||
        window.Shopify?.designMode === true ||
        !('IntersectionObserver' in window)
      ) {
        this.#activate();
        return;
      }

      this.#observeForActivation();
    }

    disconnectedCallback() {
      this.removeEventListener('banner-video:connected', this.#syncVideos);
      this.removeEventListener('banner-video:playing', this.#onVideoPlaying);
      this.removeEventListener('banner-video:progress', this.#onVideoProgress);
      this.removeEventListener('banner-video:ended', this.#onVideoEnded);
      this.removeEventListener('banner-video:error', this.#onVideoError);
      this.#disconnectIntersectionObserver();
      this.#cancelCarouselWait();
      this.#teardownAutoplay();
      this.#destroy();
      this.#hasActivated = false;
    }

    #disconnectIntersectionObserver() {
      this.#intersectionObserver?.disconnect();
      this.#intersectionObserver = null;
    }

    #cancelCarouselWait() {
      if (this.#rafId !== null) {
        cancelAnimationFrame(this.#rafId);
        this.#rafId = null;
      }
    }

    #observeForActivation() {
      if (this.#intersectionObserver || this.#hasActivated) return;

      this.#intersectionObserver = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) {
            this.#activate();
          }
        },
        {
          rootMargin: '300px 0px',
          threshold: 0,
        }
      );

      this.#intersectionObserver.observe(this);
    }

    #activate() {
      if (this.#hasActivated || !this.isConnected) return;

      this.#hasActivated = true;
      this.#disconnectIntersectionObserver();
      this.#init();
    }

    #destroy() {
      this.#cancelSlideAccessibility();

      this.querySelectorAll('banner-video').forEach((video) => {
        if (typeof video.setActive === 'function') video.setActive(false);
      });

      if (this.#carousel) {
        this.#carousel.off('ready change settle refresh', this.#scheduleSlideAccessibility);
        this.#carousel.off('ready change', this.#syncVideos);
        this.#clearSlideAccessibility();
        this.#carousel.destroy();
        this.#carousel = null;
      }
      this.carousel = null;
      this.classList.remove('is-carousel-active');
    }

    #clearSlideAccessibility() {
      this.#managedSlides.forEach((slide) => {
        slide.removeAttribute('inert');
        slide.removeAttribute('aria-hidden');
      });
      this.#managedSlides.clear();
    }

    #init() {
      if (!this.#hasActivated || this.#carousel || this.#rafId !== null) return;

      const start = () => {
        this.#rafId = null;

        if (!this.isConnected || !this.#hasActivated || this.#carousel) return;

        if (!window.Carousel) {
          this.#rafId = requestAnimationFrame(start);
          return;
        }

        const navigation = this.dataset.navigation === 'true';
        const dots = this.dataset.dots !== 'false';
        const isPeek = this.dataset.layout === 'peek';

        const options = {
          Dots: dots,
          Navigation: navigation,
          infinite: true,
          center: false,
          fill: false,
          slidesPerPage: 1,
          breakpoints: {
            '(max-width: 992px)': {
              Navigation: false,
            },
          },
        };

        if (isPeek) {
          options.transition = false;
        }

        const carousel = new window.Carousel(this, options);
        this.#carousel = carousel;
        this.carousel = carousel;

        carousel.on('ready', () => {
          if (this.#carousel === carousel) {
            this.classList.add('is-carousel-active');
          }
        });

        carousel.on('ready change settle refresh', this.#scheduleSlideAccessibility);
        carousel.on('ready change', this.#syncVideos);

        if (this.dataset.autoplay === 'true') {
          this.#setupAutoplay();
        }
      };

      if (this.dataset.initNextFrame === 'true') {
        this.#rafId = requestAnimationFrame(start);
      } else {
        start();
      }
    }

    #setupAutoplay() {
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

      const slideCount = this.querySelectorAll('.f-carousel__slide').length;
      if (slideCount < 2) return;

      this.#autoplayEnabled = true;
      this.#delayMs = Math.max(1000, (parseFloat(this.dataset.autoplaySpeed) || 5) * 1000);
      this.#remaining = this.#delayMs;
      this.style.setProperty('--banner-autoplay-duration', `${this.#delayMs / 1000}s`);

      this.addEventListener('pointerenter', this.#onPointerEnter);
      this.addEventListener('pointerleave', this.#onPointerLeave);
      this.addEventListener('focusin', this.#onFocusIn);
      this.addEventListener('focusout', this.#onFocusOut);

      this.#onVisibilityChange = () => {
        if (document.hidden) {
          this.#pause();
        } else {
          this.#resume();
        }
      };
      document.addEventListener('visibilitychange', this.#onVisibilityChange);

      // Restart progress on user navigation; autoplay advance reschedules itself.
      if (typeof this.#carousel?.on === 'function') {
        // The first cycle may start before the carousel knows its current slide.
        this.#carousel.on('ready', () => this.#beginCycle());
        this.#carousel.on('change', () => {
          if (this.#ignoreHoverPause) return;
          this.#beginCycle();
        });
      }

      this.#beginCycle();
    }

    #teardownAutoplay() {
      if (!this.#autoplayEnabled) return;

      this.#stopTimer();
      this.#cancelProgressRestart();
      this.#exitVideoMode();
      this.removeEventListener('pointerenter', this.#onPointerEnter);
      this.removeEventListener('pointerleave', this.#onPointerLeave);
      this.removeEventListener('focusin', this.#onFocusIn);
      this.removeEventListener('focusout', this.#onFocusOut);

      if (this.#onVisibilityChange) {
        document.removeEventListener('visibilitychange', this.#onVisibilityChange);
        this.#onVisibilityChange = null;
      }

      this.classList.remove('is-autoplay-running', 'is-autoplay-paused');
      this.#autoplayEnabled = false;
    }

    #stopTimer = () => {
      if (this.#timer) {
        clearTimeout(this.#timer);
        this.#timer = null;
      }
    };

    #cancelProgressRestart() {
      if (this.#progressRafId !== null) {
        cancelAnimationFrame(this.#progressRafId);
        this.#progressRafId = null;
      }
    }

    #restartProgress = () => {
      this.#cancelProgressRestart();
      this.classList.remove('is-autoplay-running');

      if (this.#isPaused) return;

      this.#progressRafId = requestAnimationFrame(() => {
        this.#progressRafId = requestAnimationFrame(() => {
          this.#progressRafId = null;

          if (this.isConnected && this.#autoplayEnabled && !this.#isPaused) {
            this.classList.add('is-autoplay-running');
          }
        });
      });
    };

    #beginCycle = () => {
      if (!this.#autoplayEnabled) return;

      this.#exitVideoMode();
      this.#stopTimer();

      const video = this.#getCurrentVideo();
      if (video && !video.failed) {
        this.#cancelProgressRestart();
        this.classList.remove('is-autoplay-running');
        this.#scheduleVideoStartTimeout();
        return;
      }

      this.#beginTimedCycle();
    };

    #beginTimedCycle = () => {
      this.#remaining = this.#delayMs;
      this.#restartProgress();

      if (this.#isPaused) {
        this.#stopTimer();
        return;
      }

      this.#stopTimer();
      this.#timerStart = Date.now();
      this.#timer = setTimeout(() => this.#advance(), this.#remaining);
    };

    #advance = () => {
      if (!this.#carousel || this.#isPaused) return;

      // Infinite wrap remounts/transforms the track and can spuriously fire
      // pointerleave — ignore hover pause briefly around the programmatic move.
      this.#ignoreHoverPause = true;
      this.#carousel.slideNext();

      // Always reschedule — do not rely solely on the carousel "change" event,
      // which can be skipped on some infinite wrap edge cases.
      this.#beginCycle();

      requestAnimationFrame(() => {
        setTimeout(() => {
          this.#ignoreHoverPause = false;

          // Recover from a false pause triggered by the infinite track flip.
          if (this.#isPaused && !this.matches(':hover') && !document.hidden) {
            this.#isPaused = false;
            this.classList.remove('is-autoplay-paused');
            this.#beginCycle();
          }
        }, 80);
      });
    };

    #pause = () => {
      if (this.#ignoreHoverPause || this.#isPaused) return;

      this.#isPaused = true;

      if (this.#timer) {
        this.#remaining = Math.max(0, this.#remaining - (Date.now() - this.#timerStart));
        this.#stopTimer();
      }

      this.classList.add('is-autoplay-paused');
    };

    #resume = () => {
      if (!this.#isPaused) return;
      if (document.hidden) return;

      this.#isPaused = false;
      this.classList.remove('is-autoplay-paused');

      if (this.#videoMode || this.#isWaitingForVideo()) return;

      this.classList.add('is-autoplay-running');

      this.#stopTimer();
      this.#timerStart = Date.now();
      this.#timer = setTimeout(() => this.#advance(), this.#remaining);
    };

    #onPointerEnter = () => {
      this.#pause();
    };

    #onPointerLeave = () => {
      this.#resume();
    };

    #onFocusIn = () => {
      this.#pause();
    };

    #onFocusOut = (event) => {
      // Only resume when focus leaves the carousel entirely.
      if (event.relatedTarget && this.contains(event.relatedTarget)) return;
      this.#resume();
    };
  }

  customElements.define('card-slider', CardSlider);
}
